// ============================================================================
// FIXED: CartPropertyCard.js - Simplified date logic with modal state management
// ============================================================================

import React, { useState, useEffect, useContext } from 'react';
import { Calendar, PenSquare, Trash, AlertCircle, CheckCircle, Truck } from 'lucide-react';
import { Tooltip, notification } from 'antd';
import EditCartModal from './EditCartModal';
import propertiesService from '../services/propertiesService';
import userRoleUtils from '../utils/userRoleUtils';
import CartContext from '../context/CartContext';
import eventBus from '../utils/eventBus';
import cartService from '../services/cartService';
import CartTaskNoteButton from './CartTaskNoteButton';
import { clearCartOrderNotes } from '../utils/cartOrderNotes';

const CartPropertyCard = ({
  propertyId,
  cartItems,
  cart,
  propertyData,
  orderSchedule = {},
  removeFromCart: propRemoveFromCart,
  handleCalendarClick,
  onUpdateCart: propOnUpdateCart,
  showVat = true
}) => {
  const contextValue = useContext(CartContext);
  const hasContext = contextValue !== undefined;

  const removeFromCart = propRemoveFromCart || (hasContext ? contextValue.removeFromCart : null);
  const onUpdateCart = propOnUpdateCart || 
    ((propertyId, items) => {
      if (hasContext && contextValue.updateCartItem) {
        contextValue.updateCartItem(propertyId, items);
      }
    });

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [property, setProperty] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState({});
  const [packs, setPacks] = useState([]);
  const [isRemoving, setIsRemoving] = useState(false);
  
  // ✅ SIMPLIFIED: Only track what we need
  const [dateTimeSet, setDateTimeSet] = useState(false);
  const [displayDate, setDisplayDate] = useState('');
  const [displayTimeSlot, setDisplayTimeSlot] = useState('');
  const [isDateValid, setIsDateValid] = useState(false);
  const [dateErrorMessage, setDateErrorMessage] = useState('');
  const [isAdminOverride, setIsAdminOverride] = useState(false);
 const CLEANING_PRODUCT_NAME = 'Collection Only';
const isCleaningOrder = cartItems && Object.values(cartItems).some(i => (i?.name || '').trim() === CLEANING_PRODUCT_NAME);
  // ✅ SIMPLIFIED: Check if date/time is set in localStorage
  const checkDateTimeInStorage = () => {
    try {
      const savedSchedule = JSON.parse(localStorage.getItem('dateIsSet') || '{}');
      const schedule = savedSchedule[propertyId];
      
      return schedule && 
             schedule.order_date && 
             schedule.arrival_time && 
             schedule.completion_time &&
             schedule.order_date.trim() !== '' &&
             schedule.arrival_time.trim() !== '' &&
             schedule.completion_time.trim() !== '';
    } catch (e) {
      return false;
    }
  };
const handleConvertToCleaning = async () => {
  if (!propertyId) return;
  await cartService.convertToCleaning(propertyId, 1);
  if (hasContext && contextValue.refreshCart) {
    contextValue.refreshCart();
  }
};
  // ✅ SIMPLIFIED: Initialize property data
  useEffect(() => {
    if (propertyData && propertyId) {
      const foundProperty = propertyData.find(p => p.id == propertyId);
      if (foundProperty) {
        setProperty(foundProperty);
        // ✅ KEY FIX: Always start with NO date set for new cart items
        initializeDateTimeState();
      }
    }
  }, [propertyData, propertyId]);

  // ✅ SIMPLIFIED: Initialize date/time state
  const initializeDateTimeState = () => {
    const hasDateTime = checkDateTimeInStorage();
    
    if (hasDateTime) {
      // Load from localStorage if exists
      loadDateTimeFromStorage();
    } else {
      // ✅ KEY FIX: Start with empty state - user must set date/time
      setDateTimeSet(false);
      setDisplayDate('');
      setDisplayTimeSlot('');
      setIsDateValid(false);
      setDateErrorMessage('Date and time selection required');
      setIsAdminOverride(false);
    }
  };

  // ✅ SIMPLIFIED: Load date/time from localStorage
  const loadDateTimeFromStorage = () => {
    try {
      const savedSchedule = JSON.parse(localStorage.getItem('dateIsSet') || '{}');
      const schedule = savedSchedule[propertyId];
      
      if (schedule && schedule.order_date && schedule.arrival_time && schedule.completion_time) {
        const date = new Date(schedule.order_date);
        const formattedDate = date.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        });
        const timeSlot = `${schedule.arrival_time} - ${schedule.completion_time}`;
        
        setDisplayDate(formattedDate);
        setDisplayTimeSlot(timeSlot);
        setDateTimeSet(true);
        
        // Validate the loaded date
        validateDateTime(schedule);
      }
    } catch (e) {
      console.error("Error loading date/time from storage:", e);
    }
  };

  // ✅ SIMPLIFIED: Validate date/time
  const validateDateTime = (schedule) => {
    if (!schedule || !schedule.order_date) {
      setIsDateValid(false);
      setDateErrorMessage('Date and time selection required');
      setIsAdminOverride(false);
      return;
    }

    if (userRoleUtils.isAdminOrImpersonating()) {
      const orderDate = new Date(schedule.order_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (orderDate < today) {
        setIsDateValid(true);
        setDateErrorMessage("Admin: Using past date");
        setIsAdminOverride(true);
      } else {
        setIsDateValid(true);
        setDateErrorMessage('');
        setIsAdminOverride(false);
      }
    } else {
      const minDate = userRoleUtils.getMinimumOrderDate();
      const orderDate = new Date(schedule.order_date);
      
      if (orderDate < new Date(minDate)) {
        setIsDateValid(false);
        setDateErrorMessage(`Date must be on or after ${new Date(minDate).toLocaleDateString('en-GB')}`);
        setIsAdminOverride(false);
      } else {
        setIsDateValid(true);
        setDateErrorMessage('');
        setIsAdminOverride(false);
      }
    }
  };

  // ✅ SIMPLIFIED: Listen for date/time updates
  useEffect(() => {
    const handleDateTimeUpdate = (data) => {
      if (data.propertyId == propertyId) {
        setDisplayDate(data.displayDate);
        setDisplayTimeSlot(data.timeSlot);
        setDateTimeSet(true);
        setIsDateValid(true);
        setDateErrorMessage('');
        setIsAdminOverride(false);
        
        if (hasContext && contextValue.refreshCart) {
          contextValue.refreshCart();
        }
      }
    };

    const unsubscribe = eventBus.on('datetime:updated', handleDateTimeUpdate);
    return unsubscribe;
  }, [propertyId, hasContext, contextValue]);

  // ✅ SIMPLIFIED: Listen for storage changes
  useEffect(() => {
    const handleStorageChange = () => {
      initializeDateTimeState();
    };
    
    const unsubscribeSchedule = eventBus.on('schedule:updated', handleStorageChange);
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      unsubscribeSchedule();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [propertyId]);

  // Calculate property total
  const displayPrice = (price) => {
    const numericPrice = parseFloat(price || 0);
    return showVat ? numericPrice : numericPrice / 1.2;
  };

  const propertyTotal = cartItems
    ? Object.values(cartItems).reduce((sum, item) => {
        const qty = parseFloat(item.quantity ?? item.qty ?? 0);
        const price = displayPrice(item.price);
        return sum + (price * qty);
      }, 0)
    : 0;

  const fetchPropertyData = async (propertyId) => {
    try {
      const response = await propertiesService.getPropertyById(propertyId);
      if (response && response.property) {
        setCategories(response.categories || []);
        setProducts(response.products || {});
        setPacks(response.packs || []);
      }
    } catch (error) {
      console.error("Error fetching property data:", error);
      notification.error({
        message: 'Connection Error',
        description: 'Could not load property details.'
      });
    }
  };

  const handleRemoveFromCart = async () => {
    if (removeFromCart && !isRemoving) {
      try {
        setIsRemoving(true);
        const removed = await removeFromCart(propertyId);
        if (removed === false) return;
        clearCartOrderNotes([propertyId]);
        
        // ✅ CLEAN UP: Remove from localStorage when removing from cart
        try {
          const savedSchedules = JSON.parse(localStorage.getItem('dateIsSet') || '{}');
          if (savedSchedules[propertyId]) {
            delete savedSchedules[propertyId];
            localStorage.setItem('dateIsSet', JSON.stringify(savedSchedules));
          }
        } catch (e) {
          console.error("Error cleaning up schedule:", e);
        }
        
      } catch (error) {
        console.error("Error removing from cart:", error);
      } finally {
        setIsRemoving(false);
      }
    }
  };

  // ✅ NEW: Handle edit modal state changes
  const handleEditModalOpen = () => {
    setIsEditModalOpen(true);
    // Notify context that a modal is open
    if (hasContext && contextValue.setIsModalOpen) {
      contextValue.setIsModalOpen(true);
    }
    // Emit event for other components
    eventBus.emit('modal:open');
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    // Notify context that modal is closed
    if (hasContext && contextValue.setIsModalOpen) {
      contextValue.setIsModalOpen(false);
    }
    // Emit event for other components
    eventBus.emit('modal:close');
  };

  if (!property) {
    return (
      <div className="bg-red-50 rounded-lg border border-red-200 p-4 sm:p-6 mb-4">
        <div className="flex items-center text-red-500 mb-4">
          <AlertCircle className="h-6 w-6 mr-2" />
          <h3 className="text-lg font-semibold">Property Not Found</h3>
        </div>
        <p className="text-red-700">Could not load details for property ID: {propertyId}</p>
        <div className="mt-4">
          <button 
            onClick={handleRemoveFromCart} 
            className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors flex items-center"
          >
            <Trash className="h-4 w-4 mr-2" />
            Remove from cart
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-golden-100 p-4 sm:p-6 mb-4">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 items-start gap-3 sm:gap-4">
          <img
            src={property.property_type_id === 1 ? '/ico/propertyHouseIco.svg' : '/ico/propertyBuildingIco.svg'}
            alt="Property Icon"
            className="h-8 w-8 flex-shrink-0"
          />
          <div className="min-w-0">
            <h2 className="break-words text-lg sm:text-xl font-semibold">{property.property_title || `Property ${propertyId}`}</h2>
            {property.agency_company_name && (
              <p className="break-words text-sm font-medium text-golden-600">{property.agency_company_name}</p>
            )}
            <p className="break-words text-sm sm:text-base text-gray-500">
              {property.apartment_number && `${property.apartment_number}, `}
              {property.apartment_address}, {property.delivery_zip_code}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between lg:justify-end lg:gap-6">
          <div className="whitespace-nowrap">
            <span className="font-medium">TOTAL:</span>
            <span className="ml-2 font-bold">£{propertyTotal.toFixed(2)}</span>
          </div>
          
          <div className="sm:text-right">
            {/* ✅ SIMPLIFIED: Show date or placeholder */}
            <div className="flex items-center text-base sm:text-xl font-bold">
              {dateTimeSet ? (
                <span>{displayDate}</span>
              ) : (
                <span 
                  className="text-gray-400 text-base cursor-pointer" 
                  onClick={() => handleCalendarClick?.(propertyId, {})}
                  title="Click to set date and time"
                >
                  Set date & time
                </span>
              )}
              
              {/* Status icons */}
              {!dateTimeSet && (
                <Tooltip title="Date and time selection required">
                  <AlertCircle className="h-4 w-4 ml-2 text-red-500 animate-pulse" />
                </Tooltip>
              )}
              {dateTimeSet && !isDateValid && (
                <Tooltip title={dateErrorMessage}>
                  <AlertCircle className="h-4 w-4 ml-2 text-red-500" />
                </Tooltip>
              )}
              {dateTimeSet && isDateValid && !isAdminOverride && (
                <Tooltip title="Date and time confirmed">
                  <CheckCircle className="h-4 w-4 ml-2 text-green-500" />
                </Tooltip>
              )}
              {isAdminOverride && (
                <Tooltip title={dateErrorMessage}>
                  <AlertCircle className="h-4 w-4 ml-2 text-amber-500" />
                </Tooltip>
              )}
            </div>
            
            {/* Show time only if set */}
            {dateTimeSet && displayTimeSlot && (
              <div className="text-gray-500 text-sm">
                {displayTimeSlot}
              </div>
            )}
            
            {isAdminOverride && (
              <div className="text-xs text-amber-600">Admin: Using past date</div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <CartTaskNoteButton propertyId={propertyId} propertyTitle={property?.property_title} />
            <button 
              onClick={() => handleCalendarClick?.(propertyId, {})} 
              className={`text-golden-500 hover:text-golden-600 ${!dateTimeSet ? 'animate-pulse' : ''}`}
              title="Set date and time"
            >
              <Calendar className="h-5 w-5" />
            </button>
          {/* Convert to Cleaning */}
{!isCleaningOrder && (
  <Tooltip title="Convert this property order to Collection Only">
    <button onClick={handleConvertToCleaning} className="text-emerald-600 hover:text-emerald-700" title="Convert to Collection Only">
      <Truck className="h-5 w-5" />
    </button>
  </Tooltip>
)}

{/* Edit je sakriven kada je cleaning */}
{!isCleaningOrder && (
  <button
    onClick={() => {
      handleEditModalOpen();
      property?.encrypted_id && fetchPropertyData(property.encrypted_id);
    }}
    className="text-golden-500 hover:text-golden-600"
    title="Edit items"
  >
    <PenSquare className="h-5 w-5" />
  </button>
)}

<button onClick={handleRemoveFromCart} disabled={isRemoving} className="text-golden-500 hover:text-golden-600 disabled:opacity-50">
  <Trash className="h-5 w-5" />
</button>

{/* Badge koji vizualno označava Cleaning narudžbu */}
{isCleaningOrder && (
  <span className="px-2 py-0.5 text-xs rounded bg-emerald-50 text-emerald-700 border border-emerald-200" title="Cleaning order">
    Collection Only
  </span>
)}
           {/*  <button onClick={handleRemoveFromCart} className="text-golden-500 hover:text-golden-600">
              <Trash className="h-5 w-5" />
            </button> */}
          </div>
        </div>
      </div>

      {/* ITEMS IN CART */}
      <div className="mt-6 space-y-3">
        {cartItems &&
          Object.values(cartItems).map((item) => (
            <div key={item.id || item.name} className="flex flex-wrap items-center justify-between gap-2 text-gray-600">
              <span className="min-w-0 flex-grow break-words">
                {item.name || 'Item'} {item.type ? `(${item.type})` : ''}
              </span>
              <div className="flex shrink-0 items-center gap-4 sm:gap-8">
                x{parseFloat(item.quantity ?? item.qty).toFixed(((item.qty ?? item.quantity) % 1 === 0) ? 0 : 1)}
                <span className="w-24 text-right">
                  £{(displayPrice(item.price) * parseFloat((item.quantity ?? item.qty) || 0)).toFixed(2)}
                </span>
              </div>
            </div>
          ))}
      </div>

      {/* ✅ SIMPLIFIED: Single warning when date/time not set */}
      {!dateTimeSet && (
        <div className="mt-4 bg-red-50 text-red-700 p-3 rounded-lg flex items-start border border-red-200">
          <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
          <div className="flex-grow">
            <p className="font-medium text-sm">Date & Time Required</p>
            <p className="text-xs mt-1">
              Please set both date and time for this property before checkout.
            </p>
            <button 
              onClick={() => handleCalendarClick?.(propertyId, {})}
              className="mt-2 bg-red-100 text-red-700 px-3 py-1 rounded text-xs font-medium flex items-center hover:bg-red-200 transition-colors"
            >
              <Calendar className="h-3 w-3 mr-1" />
              Set Date & Time Now
            </button>
          </div>
        </div>
      )}

      {/* ✅ SIMPLIFIED: Show validation error only when date is set but invalid */}
      {dateTimeSet && !isDateValid && (
        <div className="mt-4 bg-red-50 text-red-700 p-3 rounded-lg flex items-start border border-red-200">
          <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
          <div className="flex-grow">
            <p className="font-medium text-sm">Invalid Date Selected</p>
            <p className="text-xs mt-1">{dateErrorMessage}</p>
            <button 
              onClick={() => handleCalendarClick?.(propertyId, {})}
              className="mt-2 bg-red-100 text-red-700 px-3 py-1 rounded text-xs font-medium flex items-center hover:bg-red-200 transition-colors"
            >
              <Calendar className="h-3 w-3 mr-1" />
              Update Date & Time
            </button>
          </div>
        </div>
      )}

      <EditCartModal
        isOpen={isEditModalOpen}
        onClose={handleEditModalClose}
        propertyId={property.encrypted_id}
        categories={categories}
        products={products}
        packs={packs}
        defaultOrder={cartItems}
        onUpdate={onUpdateCart}
      />
    </div>
  );
};

export default CartPropertyCard;
